// src/components/brands/Brand.js
import React, { useState, useEffect, useRef } from "react";
import BrandForm from "./BrandForm";
import { Plus, Search } from "lucide-react";
import { toast } from "react-toastify";
import { brandsAPI } from "../../services";

import EditIcon from "../../assets/icons/EditIcon.js";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon.js";
import NoDataIcon from "../../assets/icons/NoDataIcon.js";

import TotalCategoriesIcon from "../../assets/total-categories.svg";
import DeleteIcon from "../../assets/delete-modal-icon.svg";
import TotalBrandsIcon from "../../assets/total-brands.svg";
import { useSelector } from "react-redux";
import "../../style/brands.css";

const Brands = () => {
  const [, setBrands] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [showForm, setShowForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ brand_count: 0, brand_category: 0 });
  const [statsLoading, setStatsLoading] = useState(false);
  const { isPartsURL, isAccessoriesURL, isGodownURL } = useSelector((state) => state.domain);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBrand, setDeletingBrand] = useState(null);

  const initialFetchDone = useRef(false);
  const [isInitialMount, setIsInitialMount] = useState(true);

  // Fetch brand statistics
  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await brandsAPI.getBrandStats();
      if (response && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error("Error fetching brand stats:", error);
      // Keep default values on error
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch brands from API
  const fetchBrands = async (search = "", page = 1) => {
    setLoading(true);
    try {
      const response = await brandsAPI.getBrandsList({
        search,
        page,
        page_size: itemsPerPage,
      });

      if (response && Array.isArray(response.results)) {
        setBrands(response.results);
        setFilteredBrands(response.results);
        setTotalCount(response.count || response.results.length || 0);
      } else if (response && Array.isArray(response)) {
        // Handle case where API returns array directly
        setBrands(response);
        setFilteredBrands(response);
        setTotalCount(response.length || 0);
      } else {
        setBrands([]);
        setFilteredBrands([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch brands. Please try again.";
      toast.error(errorMessage);
      setBrands([]);
      setFilteredBrands([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Load brands and stats on component mount
  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchBrands();
      fetchStats();
      setIsInitialMount(false);
    }
  }, []);

  // Handle search with debounce
  useEffect(() => {
    if (!isInitialMount) {
      const delayedSearch = setTimeout(() => {
        setCurrentPage(1);
        fetchBrands(searchTerm, 1);
      }, 500);

      return () => clearTimeout(delayedSearch);
    }
  }, [searchTerm]);

  // Handle pagination
  useEffect(() => {
    if (initialFetchDone.current && currentPage > 1) {
      fetchBrands(searchTerm, currentPage);
    }
  }, [currentPage]);

  const handleAddBrand = async (data) => {
    try {
      setLoading(true);
      const response = await brandsAPI.createBrand(data);

      if (response) {
        toast.success("Brand created successfully!");
        setShowForm(false);
        // Refresh the brands list and stats
        fetchBrands(searchTerm, currentPage);
        fetchStats();
      }
    } catch (error) {
      console.error("Error creating brand:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create brand. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEditBrand = async (data) => {
    try {
      setLoading(true);
      const response = await brandsAPI.updateBrand(editingBrand.id, data);

      if (response) {
        toast.success("Brand updated successfully!");
        setShowForm(false);
        setEditingBrand(null);
        // Refresh the brands list and stats
        fetchBrands(searchTerm, currentPage);
        fetchStats();
      }
    } catch (error) {
      console.error("Error updating brand:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to update brand. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBrand = async (id) => {
    try {
      setLoading(true);
      const response = await brandsAPI.deleteBrand(id);

      // Handle both successful response and 204 No Content status
      if (response || response === undefined) {
        toast.success("Brand deleted successfully!");
        setShowDeleteModal(false);
        setDeletingBrand(null);
        // Refresh the brands list and stats
        fetchBrands(searchTerm, currentPage);
        fetchStats();
      }
    } catch (error) {
      console.error("Error deleting brand:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete brand. Please try again.";
      toast.error(errorMessage);
      setShowDeleteModal(false);
      setDeletingBrand(null);
    } finally {
      setLoading(false);
    }
  };

  const openEditForm = (brand) => {
    setEditingBrand(brand);
    setShowForm(true);
  };

  // const handlePageChange = (page) => {
  //   if (page >= 1 && page <= totalPages && !loading) {
  //     setCurrentPage(page);
  //   }
  // };

  // Pagination - API handles pagination, so we use all returned brands
  const currentBrands = filteredBrands;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <div className="card rounded-4 p-4 border-0 brands-container brands-layout">
      <div className="brands-top">
        {/* Header */}
        <div className="brands-header">
          <div className="brands-title">
            <h1>Brands</h1>
            <p>Manage your product catalog and inventory levels.</p>
          </div>
          <button
            className={`${
              isPartsURL
                ? "add-brand-btn-parts"
                : isAccessoriesURL
                ? "add-brand-btn-acc"
                : isGodownURL
                ? "add-brand-btn-godown"
                : "add-brand-btn"
            }`}
            onClick={() => setShowForm(true)}
          >
            <Plus className="add-brand-icon" /> Add Brand
          </button>
        </div>

        {/* Stats */}
        <div className="row mb-4">
          <div className="col-md-4">
            <div
              className="card brands-summary-card"
              style={{
                border: "1px solid #DCE3EB",
                borderRadius: "10px",
                height: "92px",
              }}
            >
              <div className="card-body text-center d-flex align-items-start justify-content-between">
                <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                  <h6
                    className="mb-0 text-muted"
                    style={{
                      fontSize: "14px",
                      color: "#6E7781",
                      fontWeight: "400",
                    }}
                  >
                    Total Brands
                  </h6>
                  <h4
                    className="mb-0 fw-semibold"
                    style={{
                      fontSize: "22px",
                      color: "#424A53",
                      fontWeight: "500",
                    }}
                  >
                    {statsLoading ? (
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      stats.brand_count
                    )}
                  </h4>
                </div>
                <div className="d-flex align-items-center justify-content-center">
                  <img src={TotalBrandsIcon} alt="Total Brands" />
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div
              className="card brands-summary-card"
              style={{
                border: "1px solid #DCE3EB",
                borderRadius: "10px",
                height: "92px",
              }}
            >
              <div className="card-body text-center d-flex align-items-start justify-content-between">
                <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                  <h6
                    className="mb-0 text-muted"
                    style={{
                      fontSize: "14px",
                      color: "#6E7781",
                      fontWeight: "400",
                    }}
                  >
                    Total Categories
                  </h6>
                  <h4
                    className="mb-0 fw-semibold"
                    style={{
                      fontSize: "22px",
                      color: "#424A53",
                      fontWeight: "500",
                    }}
                  >
                    {statsLoading ? (
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      stats.brand_category
                    )}
                  </h4>
                </div>
                <div className="d-flex align-items-center justify-content-center">
                  <img src={TotalCategoriesIcon} alt="Total Products" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="d-flex align-items-center mb-3 gap-3">
          <div className="position-relative flex-grow-1">
            <input
              type="text"
              className={`${
                isPartsURL
                  ? "brand-search-input-parts"
                  : isAccessoriesURL
                  ? "brand-search-input-acc"
                  : isGodownURL
                  ? "brand-search-input-godown"
                  : "brand-search-input"
              } text-search`}
              placeholder="Search brands name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search
              size={16}
              className="position-absolute top-50 start-0 translate-middle-y ms-2"
              style={{ color: "#8C959F" }}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="brands-table-container brands-table-wrapper">
        <table className="brands-table">
          <colgroup>
            <col style={{ width: "25%" }} />
            <col style={{ width: "25%" }} />
            <col style={{ width: "35%" }} />
            <col style={{ width: "15%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>BRANDS</th>
              <th>CATEGORY</th>
              <th>DESCRIPTION</th>
              <th style={{ textAlign: "center" }}>ACTION</th>
            </tr>
          </thead>
        </table>
        <div className="brands-table-body-wrapper">
          <table className="brands-table">
            <colgroup>
              <col style={{ width: "25%" }} />
              <col style={{ width: "25%" }} />
              <col style={{ width: "35%" }} />
              <col style={{ width: "15%" }} />
            </colgroup>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="no-brands-message">
                    <div
                      className=""
                      style={{
                        marginBottom: "4rem",
                        marginTop: "4rem",
                      }}
                    >
                      <div className="d-flex justify-content-center align-items-center">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                      <h3 style={{ marginTop: "2rem" }}>Loading brands...</h3>
                    </div>
                  </td>
                </tr>
              ) : currentBrands.length === 0 ? (
                <tr>
                  <td colSpan="4" className="no-brands-message">
                    <div
                      className=""
                      style={{
                        marginBottom: "4rem",
                        marginTop: "4rem",
                      }}
                    >
                      <NoDataIcon />
                      <h3 style={{ marginTop: "2rem" }}>No brands found</h3>
                    </div>
                  </td>
                </tr>
              ) : (
                currentBrands.map((brand, index) => (
                  <tr
                    key={brand.id}
                    style={{
                      fontWeight: "400",
                      fontSize: "14px",
                      letterSpacing: "0%",
                      color: "#57606A",
                      borderBottom: "1px solid #DCE3EB",
                    }}
                  >
                    {/* Brand Name */}
                    <td
                      style={{
                        border: "none",
                        padding: "12px 16px",
                        borderBottom:
                          index === currentBrands.length - 1
                            ? "none"
                            : "1px solid #DCE3EB",
                        borderBottomLeftRadius:
                          index === currentBrands.length - 1 ? "8px" : "0",
                        color: "#57606A",
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        {brand.image && (
                          <img
                            src={brand.image}
                            alt={brand.name}
                            className="brand-table-logo"
                            style={{
                              width: "34px",
                              height: "39px",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                          />
                        )}
                        <span>{brand.name}</span>
                      </div>
                    </td>

                    {/* Category */}
                    <td
                      style={{
                        border: "none",
                        padding: "12px 16px",
                        borderBottom:
                          index === currentBrands.length - 1
                            ? "none"
                            : "1px solid #DCE3EB",
                        color: "#57606A",
                      }}
                    >
                      {brand.categories_detail &&
                      brand.categories_detail.length > 0 ? (
                        <span
                          className="brand-category-text"
                          title={brand.categories_detail
                            .map((cat) => cat.name)
                            .join(", ")}
                        >
                          {brand.categories_detail
                            .map((cat) => cat.name)
                            .join(", ")}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>

                    {/* Description */}
                    <td
                      style={{
                        border: "none",
                        padding: "12px 16px",
                        borderBottom:
                          index === currentBrands.length - 1
                            ? "none"
                            : "1px solid #DCE3EB",
                        color: "#57606A",
                      }}
                    >
                      <span
                        className="brand-description-text"
                        title={brand.description}
                      >
                        {brand.description}
                      </span>
                    </td>

                    {/* Action buttons */}
                    <td>
                      <div className="action-buttons">
                        <button
                          className={`${
                            isPartsURL
                              ? "action-btn-parts view"
                              : isAccessoriesURL
                              ? "action-btn-acc view"
                              : "action-btn view"
                          }`}
                          onClick={() => openEditForm(brand)}
                          title="View"
                        >
                          <EditIcon
                            fill={
                              isPartsURL
                                ? "var(--primary-color-parts)"
                                : isAccessoriesURL
                                ? "var(--primary-color-acc)"
                                : isGodownURL
                                ? "var(--primary-color-godown)"
                                : "var(--primary-color-mobile)"
                            }
                          />
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => {
                            setDeletingBrand(brand);
                            setShowDeleteModal(true);
                          }}
                          title="Delete"
                        >
                          <RedDeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4 brands-pagination-sticky">
          <div className="mrph-txt size-14 color-text-semi-regular">
            Page {currentPage} of{" "}
            {Math.ceil(currentBrands.length / itemsPerPage)}
          </div>
          <div className="pagination-controls">
            <button
              className={`${
                isPartsURL
                  ? "pagination-btn-parts"
                  : isAccessoriesURL
                  ? "pagination-btn-acc"
                  : isGodownURL
                  ? "pagination-btn-godown"
                  : "pagination-btn"
              }`}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            >
              Previous
            </button>
            <button
              className={`${
                isPartsURL
                  ? "pagination-btn-next-parts"
                  : isAccessoriesURL
                  ? "pagination-btn-next-acc"
                  : isGodownURL
                  ? "pagination-btn-next-godown"
                  : "pagination-btn-next"
              }`}
              disabled={
                currentPage >= Math.ceil(currentBrands.length / itemsPerPage)
              }
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(
                    Math.ceil(currentBrands.length / itemsPerPage),
                    prev + 1
                  )
                )
              }
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal for Add/Edit */}
      {showForm && (
        <BrandForm
          brand={editingBrand}
          onSave={editingBrand ? handleEditBrand : handleAddBrand}
          onCancel={() => {
            setShowForm(false);
            setEditingBrand(null);
          }}
        />
      )}

      {/* Delete Modal */}
      {showDeleteModal && deletingBrand && (
        <>
          <div className="modal-overlay">
            <div className="delete-modal">
              <div className="modal-icon">
                <img src={DeleteIcon} alt="Delete" />
              </div>
              <h3 className="modal-title">Delete Brand</h3>
              <p className="modal-message">
                Are you sure you want to delete brand{" "}
                <b>{deletingBrand.name}</b>?
                <br />
                This action cannot be undone.
              </p>
              <div className="modal-actions">
                <button
                  className="modal-cancel-btn"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="modal-delete-btn"
                  onClick={() => handleDeleteBrand(deletingBrand.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Brands;
